'use client';

import { useEffect, useState } from 'react';
import { Mail, ArrowRight } from 'lucide-react';
import { Button } from './primitives/Button';
import { subscribeToNewsletter } from '@/exxonim/services/newsletterService';
import { SuccessCheck } from './primitives/SuccessCheck';

/**
 * NewsletterForm - Email subscription form for use inside UnifiedCtaSection.
 *
 * MOBILE: Single row - inline input + small subscribe button.
 * DESKTOP: Wider inline - input + button in a centered row.
 *
 * UX PATTERN:
 *   Idle → Subscribing... (button disabled + spinner) → Sent! (inline, animated)
 *   Error → Inline error message, user can retry
 */
export function NewsletterForm({ source }: { source?: string } = {}) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const MIN_LOADING_MS = 1200;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');
    const startedAt = Date.now();

    try {
      const res = await subscribeToNewsletter(email.trim(), source);
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_LOADING_MS) {
        await new Promise((r) => setTimeout(r, MIN_LOADING_MS - elapsed));
      }
      setAlreadySubscribed(res?.status === 'already_subscribed');
      setSubmitted(true);
    } catch (err) {
      const data = (err as { response?: { data?: { error?: string; detail?: string } } })?.response?.data;
      setSubmitError(data?.error || data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setEmail('');
    setSubmitted(false);
    setAlreadySubscribed(false);
    setSubmitError('');
  };

  // Auto-return to the input after a delay so the CTA is ready for the
  // next signup - no click required to proceed.
  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(handleReset, 20000);
    return () => clearTimeout(timer);
  }, [submitted]);

  if (submitted) {
    return (
    <div
      role="status"
      className="w-full sm:w-auto md:min-w-[22rem] rounded-2xl bg-surface ring-1 ring-border-soft px-6 py-5 animate-success-rise"
    >
        <div className="flex items-center gap-2 text-accent">
          <SuccessCheck className="h-7 w-7 shrink-0" />
          <p className="m-0 text-base font-bold text-text">
            {alreadySubscribed ? "You're already on the list." : "You're subscribed!"}
          </p>
        </div>
        <p className="m-0 mt-1.5 text-sm leading-relaxed text-text-muted">
          {alreadySubscribed
            ? 'This email is already receiving our guides and deadline reminders.'
            : 'Practical guides and deadline reminders will land in your inbox.'}
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="mt-3 text-sm font-medium text-accent underline-offset-2 hover:underline transition-colors"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full sm:w-auto md:min-w-[22rem]"
    >
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <label htmlFor="newsletter-email" className="sr-only">
          Email address
        </label>
        <div className="relative flex-1">
          <Mail
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-soft"
            aria-hidden="true"
          />
          <input
            id="newsletter-email"
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); setSubmitError(''); }}
            disabled={isSubmitting}
            placeholder="you@company.co.tz"
            aria-invalid={submitError ? true : undefined}
            aria-describedby={submitError ? 'newsletter-email-error' : undefined}
            className="h-10 sm:h-12 w-full rounded-lg border border-border-soft bg-surface pl-9 pr-3 text-sm text-text placeholder:text-text-soft focus:outline-none focus:ring-2 focus:ring-accent/40 disabled:opacity-50"
          />
        </div>
        <Button
          size="standard"
          variant="inverse"
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          aria-label="Subscribe to newsletter"
          className="shrink-0"
        >
          {isSubmitting ? 'Subscribing...' : 'Subscribe'}
          {!isSubmitting && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
        </Button>
      </div>

      {submitError && (
        <p id="newsletter-email-error" role="alert" className="mt-1.5 text-xs text-footer-heading">
          {submitError}
        </p>
      )}
      <p className="mt-2 text-xs text-text-soft">
        No spam. Unsubscribe anytime.
      </p>
    </form>
  );
}
